/**
 * GeminiAgentRuntime — Gemini native function calling agent loop.
 *
 * Replaces JSON-mode approach with proper functionDeclarations/functionCall/functionResponse
 * protocol. Supports multi-turn, programmatic confirmation, error formatting,
 * auto-retry on network errors, streaming, and tool execution timeout.
 */

import type { WebMCPTool } from "../core/tool-registry"
import type { AgentPersona } from "./persona/types"
import { requiresConfirmation, buildConfirmation, type ConfirmationResult } from "./confirmation-policy"
import { formatToolError, isRetryable } from "./error-formatter"

// ── Types ───────────────────────────────────────────────────

export type AgentContentPart =
  | { type: "text"; text: string }
  | {
      type: "tool-call"
      toolName: string
      args: Record<string, unknown>
      result: unknown
      success: boolean
      error?: string
    }

export interface AgentRunResult {
  contentParts: AgentContentPart[]
  confirmation?: ConfirmationResult
}

interface GeminiPart {
  text?: string
  functionCall?: { id?: string; name: string; args: Record<string, unknown> }
  functionResponse?: { id?: string; name: string; response: Record<string, unknown> }
}

interface GeminiContent {
  role: "user" | "model"
  parts: GeminiPart[]
}

interface GeminiAgentConfig {
  apiKey: string
  model?: string
}

// ── Constants ───────────────────────────────────────────────

const MAX_STEPS = 10
const DEFAULT_MODEL = "gemini-3-flash-preview"
const API_BASE = "https://generativelanguage.googleapis.com/v1beta/models"
const TOOL_TIMEOUT_MS = 30_000

// ── Runtime ─────────────────────────────────────────────────

export class GeminiAgentRuntime {
  private apiKey: string
  private model: string
  private contents: GeminiContent[] = []
  private pendingConfirmation?: {
    tool: WebMCPTool
    args: Record<string, unknown>
    callId: string
    callTool: (name: string, params: Record<string, unknown>) => Promise<unknown>
    persona: AgentPersona
    tools: WebMCPTool[]
    contentParts: AgentContentPart[]
    functionDeclarations: Array<{ name: string; description: string; parameters: unknown }>
    toolMap: Map<string, WebMCPTool>
  }

  constructor(config: GeminiAgentConfig) {
    this.apiKey = config.apiKey
    this.model = config.model ?? DEFAULT_MODEL
  }

  /** Get the conversation contents for bridge integration. */
  getContents(): GeminiContent[] {
    return this.contents
  }

  /** Restore conversation history (e.g. from sessionStorage). */
  setContents(contents: GeminiContent[]) {
    this.contents = contents
  }

  /**
   * Run the agent loop: send user message, process function calls,
   * return text + tool-call parts.
   *
   * @param onChunk - Optional callback for streaming text chunks
   */
  async run(
    userMessage: string,
    tools: WebMCPTool[],
    persona: AgentPersona,
    callTool: (name: string, params: Record<string, unknown>) => Promise<unknown>,
    onChunk?: (text: string) => void,
  ): Promise<AgentRunResult> {
    // Filter tools by persona
    const filteredTools = tools.filter((t) => persona.toolFilter(t))

    const functionDeclarations = filteredTools.map((t) => ({
      name: t.name,
      description: t.description,
      parameters: t.inputSchema,
    }))

    // Trim history to prevent oversized requests (keep last 20 turns max)
    const MAX_HISTORY = 20
    if (this.contents.length > MAX_HISTORY) {
      this.contents = this.contents.slice(-MAX_HISTORY)
    }

    // Append user message to existing conversation (multi-turn support)
    this.contents.push({ role: "user", parts: [{ text: userMessage }] })

    const contentParts: AgentContentPart[] = []
    const toolMap = new Map(filteredTools.map((t) => [t.name, t]))

    for (let step = 0; step < MAX_STEPS; step++) {
      // Use streaming when onChunk callback provided, otherwise standard call
      let parts: GeminiPart[]
      let modelRole: string

      if (onChunk) {
        const streamResult = await this.callGeminiStream(
          persona.systemPrompt,
          functionDeclarations,
          onChunk,
        )
        parts = streamResult.parts
        modelRole = streamResult.role
      } else {
        const response = await this.callGemini(persona.systemPrompt, functionDeclarations)
        parts = response.candidates?.[0]?.content?.parts ?? []
        modelRole = response.candidates?.[0]?.content?.role ?? "model"
      }

      // Add model response to conversation
      this.contents.push({ role: modelRole as "model", parts })

      // Separate text parts and function call parts
      const textParts = parts.filter((p: GeminiPart) => p.text)
      const fcParts = parts.filter((p: GeminiPart) => p.functionCall)

      // If no function calls, this is the final answer
      if (fcParts.length === 0) {
        for (const tp of textParts) {
          contentParts.push({ type: "text", text: tp.text! })
        }
        return { contentParts }
      }

      // Process each function call
      for (const fcPart of fcParts) {
        const fc = fcPart.functionCall!
        const tool = toolMap.get(fc.name)
        const callId = fc.id ?? `auto-${step}`

        if (!tool) {
          // Tool not found — send error response
          const errResponse = { error: `Tool "${fc.name}" not available` }
          this.contents.push({
            role: "user",
            parts: [{ functionResponse: { id: callId, name: fc.name, response: errResponse } }],
          })
          contentParts.push({
            type: "tool-call",
            toolName: fc.name,
            args: fc.args ?? {},
            result: null,
            success: false,
            error: `Tool "${fc.name}" not available`,
          })
          continue
        }

        // Check confirmation policy
        if (requiresConfirmation(tool, fc.args)) {
          const confirmation = buildConfirmation(tool, fc.args ?? {}, callId)
          this.pendingConfirmation = {
            tool,
            args: fc.args ?? {},
            callId,
            callTool,
            persona,
            tools,
            contentParts,
            functionDeclarations,
            toolMap,
          }
          return { contentParts, confirmation }
        }

        // Auto-execute the tool with retry and timeout
        await this.executeToolWithRetry(
          fc.name,
          fc.args ?? {},
          callId,
          callTool,
          contentParts,
        )
      }
    }

    // MAX_STEPS exceeded
    contentParts.push({
      type: "text",
      text: "I've taken several actions but couldn't complete the request. Could you try breaking it into smaller steps?",
    })
    return { contentParts }
  }

  /**
   * Resume after user confirms or rejects a pending tool execution.
   */
  async resumeAfterConfirmation(
    callId: string,
    approved: boolean,
  ): Promise<AgentRunResult> {
    if (!this.pendingConfirmation || this.pendingConfirmation.callId !== callId) {
      return {
        contentParts: [{ type: "text", text: "No pending confirmation found." }],
      }
    }

    const {
      tool,
      args,
      callTool,
      contentParts,
      persona,
      functionDeclarations,
      toolMap,
    } = this.pendingConfirmation
    this.pendingConfirmation = undefined

    if (!approved) {
      // Send rejection to Gemini and continue the conversation loop
      this.contents.push({
        role: "user",
        parts: [{
          functionResponse: {
            id: callId,
            name: tool.name,
            response: { rejected: true, reason: "User rejected this action" },
          },
        }],
      })

      // Continue the loop so Gemini can suggest alternatives
      return this.continueLoop(contentParts, callTool, persona, functionDeclarations, toolMap)
    }

    // Execute the approved tool with timeout
    await this.executeToolWithRetry(
      tool.name,
      args,
      callId,
      callTool,
      contentParts,
    )

    // Continue the loop after execution
    return this.continueLoop(contentParts, callTool, persona, functionDeclarations, toolMap)
  }

  // ── Private ─────────────────────────────────────────────

  /**
   * Execute a tool with 30s timeout and one retry for retryable errors.
   */
  private async executeToolWithRetry(
    toolName: string,
    args: Record<string, unknown>,
    callId: string,
    callTool: (name: string, params: Record<string, unknown>) => Promise<unknown>,
    contentParts: AgentContentPart[],
  ): Promise<void> {
    let retried = false

    const attempt = async (): Promise<void> => {
      try {
        const result = await Promise.race([
          callTool(toolName, args),
          new Promise<never>((_, rej) =>
            setTimeout(() => rej(new Error("Tool timed out (30s)")), TOOL_TIMEOUT_MS),
          ),
        ])
        contentParts.push({
          type: "tool-call",
          toolName,
          args,
          result,
          success: true,
        })
        this.contents.push({
          role: "user",
          parts: [{
            functionResponse: {
              id: callId,
              name: toolName,
              response: typeof result === 'object' && result !== null ? result as Record<string, unknown> : { result: result ?? true },
            },
          }],
        })
      } catch (err) {
        // Retry once for retryable errors
        if (isRetryable(err) && !retried) {
          retried = true
          return attempt()
        }

        const formatted = formatToolError(err, toolName)
        contentParts.push({
          type: "tool-call",
          toolName,
          args,
          result: null,
          success: false,
          error: formatted.message,
        })
        this.contents.push({
          role: "user",
          parts: [{
            functionResponse: {
              id: callId,
              name: toolName,
              response: { error: formatted.message },
            },
          }],
        })
      }
    }

    await attempt()
  }

  /**
   * Continue the agent loop after confirmation resolution.
   * Sends conversation to Gemini for the next response.
   */
  private async continueLoop(
    contentParts: AgentContentPart[],
    callTool: (name: string, params: Record<string, unknown>) => Promise<unknown>,
    persona: AgentPersona,
    functionDeclarations: Array<{ name: string; description: string; parameters: unknown }>,
    toolMap: Map<string, WebMCPTool>,
  ): Promise<AgentRunResult> {
    // One more step to let Gemini respond after approval/rejection
    const response = await this.callGemini(persona.systemPrompt, functionDeclarations)
    const parts = response.candidates?.[0]?.content?.parts ?? []
    const modelRole = response.candidates?.[0]?.content?.role ?? "model"

    this.contents.push({ role: modelRole as "model", parts })

    const textParts = parts.filter((p: GeminiPart) => p.text)
    const fcParts = parts.filter((p: GeminiPart) => p.functionCall)

    if (fcParts.length === 0) {
      for (const tp of textParts) {
        contentParts.push({ type: "text", text: tp.text! })
      }
      return { contentParts }
    }

    // Process any follow-up function calls
    for (const fcPart of fcParts) {
      const fc = fcPart.functionCall!
      const tool = toolMap.get(fc.name)
      const callId = fc.id ?? "resume-auto"

      if (!tool) {
        this.contents.push({
          role: "user",
          parts: [{ functionResponse: { id: callId, name: fc.name, response: { error: `Tool "${fc.name}" not available` } } }],
        })
        continue
      }

      await this.executeToolWithRetry(fc.name, fc.args ?? {}, callId, callTool, contentParts)
    }

    // Get final text response
    const finalResp = await this.callGemini(persona.systemPrompt, functionDeclarations)
    const finalParts = finalResp.candidates?.[0]?.content?.parts ?? []
    this.contents.push({ role: "model", parts: finalParts })

    for (const p of finalParts) {
      if (p.text) contentParts.push({ type: "text", text: p.text })
    }

    return { contentParts }
  }

  private async callGemini(
    systemPrompt: string,
    functionDeclarations: Array<{ name: string; description: string; parameters: unknown }>,
  ): Promise<{ candidates?: Array<{ content?: { parts?: GeminiPart[]; role?: string } }> }> {
    const url = `${API_BASE}/${this.model}:generateContent?key=${this.apiKey}`

    const body: Record<string, unknown> = {
      system_instruction: { parts: [{ text: systemPrompt }] },
      contents: this.contents,
    }

    if (functionDeclarations.length > 0) {
      body.tools = [{ functionDeclarations }]
    }

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      throw new Error(`Gemini API error: ${response.status}`)
    }

    return (await response.json()) as {
      candidates?: Array<{ content?: { parts?: GeminiPart[]; role?: string } }>
    }
  }

  /**
   * Stream response from Gemini using SSE.
   * Accumulates parts and calls onChunk for each text fragment.
   */
  private async callGeminiStream(
    systemPrompt: string,
    functionDeclarations: Array<{ name: string; description: string; parameters: unknown }>,
    onChunk: (text: string) => void,
  ): Promise<{ parts: GeminiPart[]; role: string }> {
    const url = `${API_BASE}/${this.model}:streamGenerateContent?alt=sse&key=${this.apiKey}`

    const body: Record<string, unknown> = {
      system_instruction: { parts: [{ text: systemPrompt }] },
      contents: this.contents,
    }

    if (functionDeclarations.length > 0) {
      body.tools = [{ functionDeclarations }]
    }

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      throw new Error(`Gemini API error: ${response.status}`)
    }

    const allParts: GeminiPart[] = []
    let role = "model"

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error("ReadableStream not supported")
    }

    const decoder = new TextDecoder()
    let buffer = ""

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // Parse SSE "data: {...}" lines
      const lines = buffer.split("\n")
      buffer = lines.pop() ?? ""

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed.startsWith("data: ")) continue
        const jsonStr = trimmed.slice(6)
        if (jsonStr === "[DONE]") continue

        try {
          const chunk = JSON.parse(jsonStr)
          const parts = chunk.candidates?.[0]?.content?.parts ?? []
          if (chunk.candidates?.[0]?.content?.role) {
            role = chunk.candidates[0].content.role
          }

          for (const part of parts) {
            if (part.text) {
              onChunk(part.text)
              allParts.push(part)
            } else if (part.functionCall) {
              allParts.push(part)
            }
          }
        } catch {
          // Skip malformed SSE chunks
        }
      }
    }

    return { parts: allParts, role }
  }
}
